import iconComponents from '@/assets/icons/iconComponents'
import useGetCssValue from '@/hooks/useGetCssValue'
import clsx from 'clsx'
import React, { AllHTMLAttributes, FC } from 'react'
import { useNavigate } from 'react-router-dom'
import Typography from '../Typography'
import { useCrumbQueryCache, useCrumbs } from './useCrumbs'
import { ToastNotify } from '../toastManager'
import { crumbCache } from './crumbCache'

interface ICrumbs extends AllHTMLAttributes<HTMLElement> {
  separator?: React.ReactNode
  maxItems?: number
  nameMap: Map<string, string>
  ignoreCount?: number
  onExit?: () => Promise<void>
}

const RouteAwareCrumbs: FC<ICrumbs> = ({
  maxItems,
  separator = '/',
  nameMap,
  ignoreCount = 2,
  style,
  onExit,
}) => {
  useCrumbQueryCache()
  const navigate = useNavigate()
  const [crumbs, reverseMap] = useCrumbs(nameMap, ignoreCount)
  const [parentRef, parentWidth] = useGetCssValue('width', '100px')
  function gotoPage(pathName: string) {
    const crumbData = crumbCache.getCrumb(
      reverseMap.get(pathName)!?.replace(/\/$/, '')
    )
    const searchForPath = crumbData?.search ?? ''
    const stateForPath = crumbData?.state

    navigate(
      `${reverseMap.get(pathName)!?.replace(/\/$/, '')}${searchForPath}`,
      {
        state: stateForPath,
      }
    )
  }
  return (
    <nav
      ref={parentRef}
      className='flex flex-1 justify-start items-center gap-1'
      aria-label='breadcrumb'
    >
      {crumbs.map((pathName, idx) => {
        const isLast = idx === crumbs.length - 1
        const isFirst = idx === 0
        const isRoot = crumbs.length === 1

        return isFirst ? (
          <button
            key={pathName}
            className='flex justify-start items-center gap-1 cursor-pointer bg-transparent border-none p-0'
            onClick={() =>
              isRoot
                ? null
                : onExit
                ? onExit()
                    .then(() => gotoPage(pathName))
                    .catch((err) => {
                      if (err === 'NO_ERROR') return
                      ToastNotify('info', { message: err?.message ?? err })
                    })
                : gotoPage(pathName)
            }
            aria-label='Go back'
          >
            {!isRoot && (
              <iconComponents.util.ArrowLeftIcon className='stroke-jaa-shades-gray-2 w-6 h-6' />
            )}
            <Typography
              as='span'
              className={clsx(
                'md sm:h4 lg:text-[28px] capitalize lg:leading-[40px] text-jaa-shades-gray-2 hover:text-jaa-shades-black font-semibold sm:font-medium',
                isRoot &&
                  '!text-jaa-shades-black border-l-2 border-jaa-teal-100 pl-2 cursor-default'
              )}
            >
              {parseInt(parentWidth) > window.innerWidth ? '...' : crumbs[0]}
            </Typography>
          </button>
        ) : (
          <div key={pathName} className='flex justify-start items-center gap-1'>
            {/* Visually hidden separator, announced by screen readers */}
            <span
              className={clsx(
                'w-0.5 h-9 rounded-lg',
                isLast ? 'bg-jaa-teal-100' : 'bg-jaa-shades-gray-2'
              )}
              aria-hidden='true'
            ></span>

            <button
              onClick={() =>
                isLast
                  ? null
                  : onExit
                  ? onExit()
                      .then(() => gotoPage(pathName))
                      .catch((err) => {
                        if (err === 'NO_ERROR') return
                        ToastNotify('info', { message: err?.message ?? err })
                      })
                  : gotoPage(pathName)
              }
              className='flex justify-start items-center gap-1 cursor-pointer bg-transparent border-none p-0'
              aria-current={isLast ? 'page' : undefined}
              disabled={isLast}
            >
              <Typography
                as='span'
                className={clsx(
                  'md sm:h4 lg:text-[28px] capitalize lg:leading-[40px] hover:text-jaa-shades-black font-semibold sm:font-medium max-w-[147px] md:max-w-[205px] lg:max-w-[362px] whitespace-nowrap overflow-hidden text-ellipsis',
                  isLast ? 'text-jaa-shades-black' : 'text-jaa-shades-gray-2'
                )}
              >
                {pathName}
              </Typography>
            </button>
          </div>
        )
      })}
    </nav>
  )
}

export default RouteAwareCrumbs
